import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useSessionContext } from '../contexts/SessionContext';
import type { ServerMessage } from '@roll1/shared';

/**
 * useUserUiPrefs — cross-device persistent UI state, server-backed
 * via `User.uiPreferences` JSONB column (Feature B-03).
 *
 * Replaces `localStorage` for HUD UI state (mode, position, last tab,
 * panel collapsed flags, etc.) so the user's choices follow them across
 * browsers / devices.
 *
 * Pattern (GM Studio 5.2):
 *   - ONE store per user for the whole tab, shared by every hook instance
 *     (HUD, ThemeSync) and kept across remounts: Studio → table remounts the
 *     HUD, and it must start from the known prefs, not from `{}` (the old
 *     per-instance state painted the defaults and jumped when the reply came).
 *   - `user:ui-prefs-get` once per join of the socket (the first one and every
 *     rejoin) — not per render: `mp` is a new object on every SessionProvider
 *     render, and keying the request on it re-fetched on every such render.
 *   - `setPref` / `setPrefs`: optimistic local value, debounced
 *     `user:ui-prefs-set`.
 *   - Replies are ordered, not compared: the store has ONE request on the wire
 *     at a time, and the server answers each with `correlationId` = the wire id
 *     `mp.send` returned. So replies arrive in request order, a reply to an
 *     older request than the one shown is stale and dropped, and a local value
 *     is confirmed only by the reply to the very write that carried it (equal
 *     values prove nothing: A → B → A). Until then it stays on top of what the
 *     server shows. A write the server never answers is sent again, then given
 *     up — the UI falls back to the server's value instead of pretending it
 *     was saved.
 *   - One-time migration: if server prefs are empty AND localStorage has
 *     legacy HUD_UI_STORAGE_KEY content, upload it before clearing the
 *     local key. Gated by а sessionStorage flag к avoid retry on remount.
 *
 * Anonymous users (not logged in) skip server I/O entirely и fall back
 * к pure local state — the server has no User row к persist into.
 */

const LEGACY_LOCAL_KEY = 'roll1:character-hud-ui';
const MIGRATION_FLAG_KEY = 'roll1:ui-prefs-migrated';

// Coalesce rapid pref writes into one network send. Several writers (hudState,
// lastTab, hudPos, collapsed, pins, theme) can fire within the same tick; each
// `user:ui-prefs-set` is a `prisma.user.update`, and a burst can exhaust the DB
// connection pool. Local state still updates optimistically & immediately —
// only the send is debounced.
const PREFS_FLUSH_DEBOUNCE_MS = 400;
// A request without a reply by then is treated as lost (the next one may go).
const REPLY_TIMEOUT_MS = 3000;
// Sends of one value before it is given up as unsaved.
const MAX_SEND_ATTEMPTS = 2;

type Prefs = Record<string, unknown>;
type PrefsMessage = { type: 'user:ui-prefs-get' } | { type: 'user:ui-prefs-set'; prefs: Prefs };
type PrefsSend = (msg: PrefsMessage) => string | undefined;

export interface UseUserUiPrefsReturn {
  prefs: Record<string, unknown>;
  setPref: (key: string, value: unknown) => void;
  setPrefs: (patch: Record<string, unknown>) => void;
  /** Send the pending changes now instead of after the debounce (a setting another actor reads on the server at once). */
  flush: () => void;
  /**
   * True once the server's authoritative prefs have arrived (or immediately
   * for anonymous users, who have no server side). Auto-writers (effects that
   * push local state to the server without a user action, e.g. ThemeSync)
   * MUST wait for this — writing against the initial `{}` overwrites the
   * cross-device value before it is ever seen.
   */
  ready: boolean;
}

function readLegacyLocalStorage(): Prefs | null {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Prefs;
    }
    return null;
  } catch {
    return null;
  }
}

function clearLegacyLocalStorage(): void {
  try {
    localStorage.removeItem(LEGACY_LOCAL_KEY);
  } catch {
    /* localStorage unavailable — ignore */
  }
}

function isMigrationDone(): boolean {
  try {
    return sessionStorage.getItem(MIGRATION_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function markMigrationDone(): void {
  try {
    sessionStorage.setItem(MIGRATION_FLAG_KEY, '1');
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

interface PrefsSnapshot {
  /** The server's prefs with the unacknowledged local writes on top. */
  prefs: Prefs;
  /** The server's prefs have arrived at least once. */
  synced: boolean;
}

interface PrefsRequest {
  id: string;
  /** Send order within the store. */
  seq: number;
  /** null = get. */
  patch: Prefs | null;
}

interface PrefsStore {
  userId: string | null;
  /** Shown before the first server reply (legacy localStorage). */
  bootstrap: Prefs;
  server: Prefs | null;
  /** seq of the request whose reply `server` is (-1: none yet). */
  serverSeq: number;
  /** The user's values not acknowledged yet by the reply to their own write. */
  local: Prefs;
  /** key → seq of the sent write carrying local[key]; absent while queued. */
  carrier: Map<string, number>;
  /** Unanswered sends per key. */
  attempts: Map<string, number>;
  /** Writes not sent yet. */
  pending: Prefs;
  getWanted: boolean;
  getAttempts: number;
  /** Requests sent on the current socket and not answered yet, by wire id. */
  sent: Map<string, PrefsRequest>;
  /** The one request on the wire. */
  inFlight: PrefsRequest | null;
  replyTimer: ReturnType<typeof setTimeout> | null;
  flushTimer: ReturnType<typeof setTimeout> | null;
  nextSeq: number;
  send: PrefsSend | null;
  /** joinSeq the store last joined with. */
  joined: number | null;
  mounted: number;
  snapshot: PrefsSnapshot;
  listeners: Set<() => void>;
}

const stores = new Map<string, PrefsStore>();

function storeFor(userId: string | null): PrefsStore {
  const key = userId ?? '';
  let store = stores.get(key);
  if (!store) {
    const bootstrap = readLegacyLocalStorage() ?? {};
    store = {
      userId,
      bootstrap,
      server: null,
      serverSeq: -1,
      local: {},
      carrier: new Map(),
      attempts: new Map(),
      pending: {},
      getWanted: false,
      getAttempts: 0,
      sent: new Map(),
      inFlight: null,
      replyTimer: null,
      flushTimer: null,
      nextSeq: 0,
      send: null,
      joined: null,
      mounted: 0,
      snapshot: { prefs: bootstrap, synced: false },
      listeners: new Set(),
    };
    stores.set(key, store);
  }
  return store;
}

function publish(store: PrefsStore): void {
  store.snapshot = {
    prefs: { ...(store.server ?? store.bootstrap), ...store.local },
    synced: store.server !== null,
  };
  for (const listener of [...store.listeners]) listener();
}

/** Put the request on the wire: it is the only one until answered or timed out. */
function dispatch(store: PrefsStore, msg: PrefsMessage, patch: Prefs | null): PrefsRequest | null {
  const id = store.send?.(msg);
  if (id === undefined) return null; // dropped (socket not open) — retried on the next join
  const request: PrefsRequest = { id, seq: store.nextSeq++, patch };
  store.sent.set(id, request);
  store.inFlight = request;
  store.replyTimer = setTimeout(() => replyTimedOut(store, request), REPLY_TIMEOUT_MS);
  return request;
}

/** Send the next request if the wire is free: the get first, then the queued writes. */
function pump(store: PrefsStore): void {
  if (!store.userId || !store.send || store.inFlight) return;
  if (store.getWanted) {
    if (dispatch(store, { type: 'user:ui-prefs-get' }, null)) store.getWanted = false;
    return;
  }
  if (store.flushTimer) return; // writes are still being coalesced
  const patch = store.pending;
  if (Object.keys(patch).length === 0) return;
  const request = dispatch(store, { type: 'user:ui-prefs-set', prefs: patch }, patch);
  if (!request) return;
  store.pending = {};
  for (const key of Object.keys(patch)) store.carrier.set(key, request.seq);
}

function scheduleFlush(store: PrefsStore): void {
  if (!store.userId) return; // anonymous — local only
  if (store.flushTimer) clearTimeout(store.flushTimer);
  store.flushTimer = setTimeout(() => {
    store.flushTimer = null;
    pump(store);
  }, PREFS_FLUSH_DEBOUNCE_MS);
}

function flushNow(store: PrefsStore): void {
  if (store.flushTimer) {
    clearTimeout(store.flushTimer);
    store.flushTimer = null;
  }
  pump(store);
}

function writeLocal(store: PrefsStore, patch: Prefs): void {
  store.local = { ...store.local, ...patch };
  store.pending = { ...store.pending, ...patch };
  for (const key of Object.keys(patch)) {
    // A newer value: whatever is on the wire for this key no longer confirms it.
    store.carrier.delete(key);
    store.attempts.delete(key);
  }
  publish(store);
  scheduleFlush(store);
}

function replyTimedOut(store: PrefsStore, request: PrefsRequest): void {
  if (store.inFlight !== request) return;
  store.inFlight = null;
  store.replyTimer = null;
  // The request stays in `sent`: a late reply still counts if nothing newer is shown.
  if (request.patch === null) {
    store.getAttempts += 1;
    store.getWanted = store.getAttempts < MAX_SEND_ATTEMPTS;
  } else {
    let gaveUp = false;
    for (const key of Object.keys(request.patch)) {
      if (store.carrier.get(key) !== request.seq) continue;
      store.carrier.delete(key);
      const attempts = (store.attempts.get(key) ?? 0) + 1;
      if (attempts < MAX_SEND_ATTEMPTS) {
        store.attempts.set(key, attempts);
        store.pending = { ...store.pending, [key]: store.local[key] };
      } else {
        // Never acknowledged: stop showing it as saved.
        store.attempts.delete(key);
        const local = { ...store.local };
        delete local[key];
        store.local = local;
        gaveUp = true;
      }
    }
    if (gaveUp) publish(store);
  }
  pump(store);
}

function applyReply(store: PrefsStore, prefs: Prefs, correlationId: string | undefined): void {
  const request = correlationId === undefined ? undefined : store.sent.get(correlationId);
  // Not an answer to this store's request on this socket: it cannot be ordered.
  if (!request) return;
  store.sent.delete(request.id);
  if (store.inFlight === request) {
    if (store.replyTimer) clearTimeout(store.replyTimer);
    store.replyTimer = null;
    store.inFlight = null;
  }
  if (request.seq > store.serverSeq) {
    // One request at a time: a reply to a later request already reflects every
    // earlier one, so it replaces what is shown; an older reply is stale.
    const first = store.server === null;
    store.server = prefs;
    store.serverSeq = request.seq;
    if (request.patch !== null) {
      const local = { ...store.local };
      for (const key of Object.keys(request.patch)) {
        if (store.carrier.get(key) !== request.seq) continue; // a newer value is queued
        store.carrier.delete(key);
        store.attempts.delete(key);
        delete local[key];
      }
      store.local = local;
    }
    publish(store);
    if (first && Object.keys(prefs).length === 0 && !isMigrationDone()) {
      // One-time legacy → server upload once we know the server side is empty.
      const legacy = readLegacyLocalStorage();
      if (legacy && Object.keys(legacy).length > 0) {
        writeLocal(store, legacy);
        clearLegacyLocalStorage();
      }
      markMigrationDone();
    }
  }
  pump(store);
}

function join(store: PrefsStore, joinSeq: number): void {
  if (store.joined === joinSeq) return;
  store.joined = joinSeq;
  // A new socket: requests sent on the old one went with it. Their writes are
  // queued again (the same values — idempotent), the prefs asked again first.
  if (store.replyTimer) clearTimeout(store.replyTimer);
  store.replyTimer = null;
  store.inFlight = null;
  store.sent.clear();
  for (const key of store.carrier.keys()) {
    store.pending = { ...store.pending, [key]: store.local[key] };
  }
  store.carrier.clear();
  store.getWanted = true;
  store.getAttempts = 0;
  pump(store);
}

export function useUserUiPrefs(): UseUserUiPrefsReturn {
  const ctx = useSessionContext() as Partial<ReturnType<typeof useSessionContext>>;
  // Tolerant of partial mocks (vi.mock в unit tests may omit `auth`).
  const mp = ctx.mp;
  const auth = ctx.auth;
  const userId = auth?.user?.id ?? null;
  // The stable members of mp, not mp itself (a new object on every render).
  const send = mp?.send as PrefsSend | undefined;
  const onMessage = mp?.onMessage;
  const joinSeq = mp?.joinSeq ?? 0;
  const store = storeFor(userId);

  const subscribe = useCallback((listener: () => void) => {
    store.listeners.add(listener);
    return () => { store.listeners.delete(listener); };
  }, [store]);
  const getSnapshot = useCallback(() => store.snapshot, [store]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Server → store. Replies go to this socket, not to a hook instance, so
  // every instance listens; a reply is applied once (matched by its id).
  useEffect(() => {
    if (!onMessage) return;
    return onMessage((msg: ServerMessage) => {
      if (msg.type !== 'user:ui-prefs-updated') return;
      if (userId && msg.userId !== userId) return; // ignore other users' echoes
      applyReply(store, (msg.prefs ?? {}) as Prefs, msg.correlationId);
    });
  }, [onMessage, userId, store]);

  // Fetch once per join (anonymous OR test mock without send — no server I/O).
  useEffect(() => {
    if (!userId || !send) return;
    store.send = send;
    join(store, joinSeq);
  }, [store, userId, send, joinSeq]);

  // The last instance out sends a pending change now (leaving the session may
  // close the socket before the debounce fires).
  useEffect(() => {
    store.mounted += 1;
    return () => {
      store.mounted -= 1;
      if (store.mounted === 0) flushNow(store);
    };
  }, [store]);

  const setPrefs = useCallback((patch: Record<string, unknown>) => {
    writeLocal(store, patch);
  }, [store]);

  const setPref = useCallback(
    (key: string, value: unknown) => {
      setPrefs({ [key]: value });
    },
    [setPrefs],
  );

  // Anonymous users have no server side to wait for.
  const flush = useCallback(() => { flushNow(store); }, [store]);
  return { prefs: snapshot.prefs, setPref, setPrefs, flush, ready: userId === null || snapshot.synced };
}
